/* eslint-disable */
import ExcelJS from "exceljs";
import { datat as algeriaCommunes } from "./data";

// delivery company json files
import ecotrackCommunes from "../../../utils/data_livraison/data_ecotrack.json";
import ecomCommunes from "../../../utils/data_livraison/data_ecom.json";
import guepexCommunes from "../../../utils/data_livraison/data_guepex.json";
import maystroCommunes from "../../../utils/data_livraison/data_maystro.json";

// shared helpers
const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const getPostcode = (c) => c?.fields?.postcode ?? c?.postcode ?? null;

function resolveLocation(stateArabic, communeArabic) {
  const stateKey = normalize(stateArabic);
  const communeKey = normalize(communeArabic);

  if (!stateKey) {
    return {
      wilayaCode: "",
      wilayaAscii: "",
      communeAscii: "",
      postcode: "",
    };
  }

  const inWilaya = algeriaCommunes.filter(
    (c) =>
      normalize(c.wilaya_name) === stateKey ||
      normalize(c.wilaya_name_ascii) === stateKey ||
      String(c.wilaya_code) === String(stateArabic).trim() ||
      String(c.fields?.wilaya) === String(stateArabic).trim(),
  );

  const row =
    inWilaya.find((c) => normalize(c.commune_name) === communeKey) ||
    inWilaya.find((c) => normalize(c.commune_name_ascii) === communeKey) ||
    algeriaCommunes.find((c) => normalize(c.commune_name) === communeKey) ||
    inWilaya[0] ||
    null;

  if (!row) {
    return {
      wilayaCode: "",
      wilayaAscii: stateArabic || "",
      communeAscii: communeArabic || "",
      postcode: "",
    };
  }

  return {
    wilayaCode: String(row.wilaya_code || row.fields?.wilaya || ""),
    wilayaAscii: row.wilaya_name_ascii || "",
    communeAscii: row.commune_name_ascii || row.wilaya_name_ascii || "",
    postcode: String(getPostcode(row) || ""),
  };
}

function resolveGuepexLocation(stateArabic, communeArabic) {
  const communeKey = normalize(communeArabic);
  const stateKey = normalize(stateArabic);

  // match on arabic name
  let match = guepexCommunes.find(
    (c) => c.name_ar && normalize(c.name_ar) === communeKey,
  );

  // match on french name
  if (!match) {
    match = guepexCommunes.find((c) => normalize(c.name) === communeKey);
  }

  // match by wilaya and commune
  if (!match && stateKey) {
    const wilayaRow = algeriaCommunes.find(
      (c) =>
        normalize(c.wilaya_name) === stateKey ||
        normalize(c.wilaya_name_ascii) === stateKey,
    );

    if (wilayaRow) {
      const wilayaId = Number(wilayaRow.wilaya_code);
      match = guepexCommunes.find(
        (c) =>
          Number(c.wilaya_id) === wilayaId &&
          (normalize(c.name_ar) === communeKey ||
            normalize(c.name) === communeKey),
      );
    }
  }

  if (match) {
    return {
      wilayaAscii: match.wilaya_name,
      communeAscii: match.name,
      matched: true,
    };
  }

  // fallback
  const fallback = resolveLocation(stateArabic, communeArabic);
  return {
    wilayaAscii: fallback.wilayaAscii,
    communeAscii: fallback.communeAscii,
    matched: false,
  };
}

function getProductInfo(order) {
  const products = Array.isArray(order.products) ? order.products : [];

  // multi products in the same order:
  // abcde*2;fghij*1;..."  and quantity = 1
  if (products.length > 1) {
    const article = products
      .map((p) => {
        const name = String(p?.name || "").trim();
        const short = name.slice(0, 10);
        const q = p?.quantity ?? 1;
        return `${short}*${q}`;
      })
      .join(";");

    const ref = products[0]?.sku || products[0]?.id || "";
    return { article, qty: 1, ref };
  }

  // single product
  const first = products[0] || null;
  const article =
    (order.nameproduct && order.nameproduct !== "."
      ? order.nameproduct
      : null) ||
    first?.name ||
    "";
  const qty = order.qty ?? first?.quantity ?? 1;
  const ref = first?.sku || first?.id || "";
  return { article, qty, ref };
}

function resolveStopdeskCode(order, deskCodesByOrderId = {}) {
  if (deskCodesByOrderId[order.id]) {
    return String(deskCodesByOrderId[order.id]).trim();
  }
  const saved =
    order.selectedDesk || order.ecomStopdeskCode || order.stopdeskCode || "";
  return saved ? String(saved).trim() : "";
}

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function findHeaderMap(sheet, anchorHeader = "Nom Complet") {
  let headerRowNumber = 1;
  const headerMap = {};

  sheet.eachRow((row, rowNumber) => {
    let found = false;
    row.eachCell((cell) => {
      const text = String(cell.value ?? "")
        .replace(/\s+/g, " ")
        .trim();
      if (normalize(text) === normalize(anchorHeader)) {
        found = true;
        headerRowNumber = rowNumber;
      }
    });
    if (found) {
      row.eachCell((cell, colNumber) => {
        const text = String(cell.value ?? "")
          .replace(/\s+/g, " ")
          .trim();
        if (text) headerMap[text] = colNumber;
      });
    }
  });

  return { headerRowNumber, headerMap };
}

function clearDataRows(sheet, startDataRow) {
  if (sheet.rowCount < startDataRow) return;
  for (let r = sheet.rowCount; r >= startDataRow; r--) {
    const row = sheet.getRow(r);
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.value = null;
      cell.style = {
        ...cell.style,
        fill: { type: "pattern", pattern: "none" },
      };
    });
  }
}

function writeRowToSheet(
  sheet,
  rowNumber,
  headerMap,
  rowObj,
  isInvalid = false,
) {
  const excelRow = sheet.getRow(rowNumber);

  Object.entries(headerMap).forEach(([headerName, colNumber]) => {
    const cell = excelRow.getCell(colNumber);
    cell.value = rowObj[headerName] ?? "";

    // clone the style instead of mutating the (possibly shared) style object
    cell.style = {
      ...cell.style,
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: isInvalid ? "FFFF6B6B" : "FFFFFFFF" },
      },
    };
  });

  excelRow.commit();
}

// valid algerian phone formats (ecotrack)
function isValidPhone(phone) {
  const p = String(phone || "").replace(/\s+/g, "");
  return (
    /^0[5-7]\d{8}$/.test(p) || // 0661011000
    /^00213[5-7]\d{8}$/.test(p) || // 00213661011001
    /^\+213[5-7]\d{8}$/.test(p) // +213661111011
  );
}

// convert local algerian number to international format (zr express)
function toInternationalPhone(phone) {
  const p = String(phone || "").replace(/\s+/g, "");

  // already international
  if (/^\+213[5-7]\d{8}$/.test(p)) return p;
  if (/^00213[5-7]\d{8}$/.test(p)) return p;

  // local 05/06/07 to +213
  if (/^0[5-7]\d{8}$/.test(p)) {
    return "+213" + p.slice(1);
  }

  return p;
}

function isValidInternationalPhone(phone) {
  const p = String(phone || "").replace(/\s+/g, "");
  return /^\+213[5-7]\d{8}$/.test(p) || /^00213[5-7]\d{8}$/.test(p);
}

function splitName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/);
  if (parts.length === 0) return { nom: "", prenom: "" };
  if (parts.length === 1) return { nom: parts[0], prenom: "" };
  return {
    nom: parts[0],
    prenom: parts.slice(1).join(" "),
  };
}

// resolve wilaya_id from data_ecotrack.json using arabic commune name
function resolveEcotrackWilaya(stateArabic, communeArabic) {
  const communeKey = normalize(communeArabic);
  const stateKey = normalize(stateArabic);

  // 1. Exact match on Arabic name
  let match = ecotrackCommunes.find(
    (c) => c.nom_ar && normalize(c.nom_ar) === communeKey,
  );

  // 2. Exact match on French name
  if (!match) {
    match = ecotrackCommunes.find((c) => normalize(c.nom) === communeKey);
  }

  // 3. Match by wilaya + commune
  if (!match && stateKey) {
    const wilayaRow = algeriaCommunes.find(
      (c) =>
        normalize(c.wilaya_name) === stateKey ||
        normalize(c.wilaya_name_ascii) === stateKey,
    );

    if (wilayaRow) {
      const wilayaId = String(wilayaRow.wilaya_code);
      match = ecotrackCommunes.find(
        (c) =>
          String(c.wilaya_id) === wilayaId &&
          (normalize(c.nom_ar) === communeKey ||
            normalize(c.nom) === communeKey),
      );
    }
  }

  if (match) {
    return {
      wilayaCode: String(match.wilaya_id),
      communeAscii: match.nom,
      matched: true,
      hasStopDesk: Boolean(match.has_stop_desk),
    };
  }

  // fallback
  const fallback = resolveLocation(stateArabic, communeArabic);
  return {
    wilayaCode: fallback.wilayaCode,
    communeAscii: fallback.communeAscii,
    matched: false,
    hasStopDesk: false,
  };
}

function resolveEcomLocation(stateArabic, communeArabic) {
  const communeKey = normalize(communeArabic);
  const stateKey = normalize(stateArabic);

  // exact match on arabic name
  let match = ecomCommunes.find(
    (c) => c.Commune_ar && normalize(c.Commune_ar) === communeKey,
  );

  // fallback: exact match on french name
  if (!match) {
    match = ecomCommunes.find((c) => normalize(c.Commune) === communeKey);
  }

  // match by wilaya + commune
  if (!match && stateKey) {
    const wilayaRow = algeriaCommunes.find(
      (c) =>
        normalize(c.wilaya_name) === stateKey ||
        normalize(c.wilaya_name_ascii) === stateKey,
    );

    if (wilayaRow) {
      const wilayaId = Number(wilayaRow.wilaya_code);
      match = ecomCommunes.find(
        (c) =>
          Number(c.Wilaya) === wilayaId &&
          (normalize(c.Commune_ar) === communeKey ||
            normalize(c.Commune) === communeKey),
      );
    }
  }

  if (match) {
    return {
      wilayaCode: String(match.Wilaya),
      wilayaAscii: "",
      communeAscii: match.Commune,
      matched: true,
    };
  }

  // fallback
  const fallback = resolveLocation(stateArabic, communeArabic);
  return {
    wilayaCode: fallback.wilayaCode,
    wilayaAscii: fallback.wilayaAscii,
    communeAscii: fallback.communeAscii,
    matched: false,
  };
}

function shouldUseSingleProductTemplate(orders) {
  if (!orders?.length) return true;

  // every order must have exactly 1 product
  const allSingle = orders.every(
    (o) => Array.isArray(o.products) && o.products.length === 1,
  );
  if (!allSingle) return false;

  // that single product must be the same for all orders (same id preferred else same name)
  const firstId =
    orders[0].products[0]?.id || orders[0].products[0]?.name || "";
  return orders.every((o) => {
    const id = o.products[0]?.id || o.products[0]?.name || "";
    return id === firstId;
  });
}

function buildMaystroWilayaMap(workbook, sheetName = "wilayas") {
  const sheet = workbook.getWorksheet(sheetName);
  const map = new Map();
  if (!sheet) return map;

  for (let col = 1; col <= sheet.columnCount; col++) {
    const code = sheet.getRow(1).getCell(col).value;
    const name = sheet.getRow(2).getCell(col).value;
    if (code != null && name) {
      map.set(String(code).trim(), String(name).trim());
    }
  }
  return map;
}

function resolveMaystroLocation(stateArabic, communeArabic, wilayaMap) {
  const communeKey = normalize(communeArabic);
  const stateKey = normalize(stateArabic);

  let match = maystroCommunes.find(
    (c) => c.name_ar && normalize(c.name_ar) === communeKey,
  );

  if (!match) {
    match = maystroCommunes.find((c) => normalize(c.name) === communeKey);
  }

  if (!match && stateKey) {
    const wilayaRow = algeriaCommunes.find(
      (c) =>
        normalize(c.wilaya_name) === stateKey ||
        normalize(c.wilaya_name_ascii) === stateKey,
    );
    if (wilayaRow) {
      const wilayaId = Number(wilayaRow.wilaya_code);
      match = maystroCommunes.find(
        (c) =>
          Number(c.wilaya) === wilayaId &&
          (normalize(c.name_ar) === communeKey ||
            normalize(c.name) === communeKey),
      );
    }
  }

  if (!match) {
    return { wilayaAscii: "", communeAscii: "", matched: false };
  }

  const wilayaCode = String(match.wilaya);
  const wilayaAscii = wilayaMap.get(wilayaCode) || "";

  if (!wilayaAscii) {
    return {
      wilayaAscii: "",
      communeAscii: match.name || "",
      matched: false,
    };
  }

  return {
    wilayaAscii,
    communeAscii: match.name,
    matched: true,
  };
}

function getMaxProductsCount(orders) {
  let max = 0;
  for (const o of orders || []) {
    const n = Array.isArray(o.products) ? o.products.length : 0;
    if (n > max) max = n;
  }
  return max;
}

function ensureMaystroMultiProductColumns(
  sheet,
  headerMap,
  headerRowNumber,
  maxProducts,
) {
  // template already has Produit 1..3 and Quantity 1..3
  if (maxProducts <= 3) return headerMap;

  // find the last used column from current headerMap
  let lastCol = 0;
  Object.values(headerMap).forEach((col) => {
    if (col > lastCol) lastCol = col;
  });

  const headerRow = sheet.getRow(headerRowNumber);
  const nextMap = { ...headerMap };

  for (let i = 4; i <= maxProducts; i++) {
    const produitHeader = `Produit ${i}`;
    const qtyHeader = `Quantity ${i}`;

    // Produit N
    lastCol += 1;
    headerRow.getCell(lastCol).value = produitHeader;
    nextMap[produitHeader] = lastCol;

    // Quantity N
    lastCol += 1;
    headerRow.getCell(lastCol).value = qtyHeader;
    nextMap[qtyHeader] = lastCol;
  }

  headerRow.commit();
  return nextMap;
}

// ecom config
const ECOM = {
  templateUrl: "/templates/shipping/ecomdelivery.xlsx",
  sheetName: "Commande",
  anchorHeader: "Nom Complet",
};

// dhd/ecotrack config
const ECOTRACK = {
  templateUrl: "/templates/shipping/ecotrack.xlsx",
  sheetName: "Sheet1",
  anchorHeader: "reference commande",
};

// zr express config
const ZREXPRESS = {
  templateUrl: "/templates/shipping/zrexpress.xlsx",
  sheetName: "Sheet",
  anchorHeader: "Nom Complet*",
};

// osen config
const OSEN = {
  templateUrl: "/templates/shipping/osen.xlsx",
  sheetName: "Orders",
  anchorHeader: "Type De livraison",
};

// guepex config
const GUEPEX = {
  templateUrl: "/templates/shipping/guepex.xlsx",
  sheetName: "Feuil1",
  anchorHeader: "nom",
};

// maystro config
const MAYSTRO = {
  templateUrl: "/templates/shipping/maystro.xlsx",
  sheetName: "Orders",
  anchorHeader: "Nom_du_client",
  wilayasSheetName: "wilayas",
};

const MAYSTRO_MULTI = {
  templateUrl: "/templates/shipping/maystro_multiple_products.xlsx",
  sheetName: "Orders",
  anchorHeader: "Nom_du_client",
  wilayasSheetName: "wilayas",
};

function orderToEcomRow(order, location, stopdeskCode) {
  const { article, qty, ref } = getProductInfo(order);
  const wilayaValue = location.wilayaAscii || location.wilayaCode || "";

  return {
    "Nom Complet": order.name || "",
    Téléphone: order.phone || "",
    Article: article,
    Quantité: qty,
    Adresse: order.address || "",
    "Nom ou Code Wilaya": wilayaValue,
    Commune: location.communeAscii || "",
    "Total à ramasser": order.total ?? 0,
    "ID Externe": order.orderNumber || "",
    "OUI pour Echange": "",
    "Si Stopdesk mettez le Code du stopdesk": stopdeskCode || "",
    "Ref Article": ref,
    Note: order.note || "",
  };
}

function orderToEcotrackRow(order, location) {
  const { article, qty } = getProductInfo(order);

  const row = {
    "reference commande": order.orderNumber || "",
    "nom et prenom du destinataire*": order.name || "",
    "telephone*": order.phone || "",
    "telephone 2": order.phone2 || "",
    "code wilaya*": location.wilayaCode || "",
    "wilaya de livraison": location.wilayaAscii || location.wilayaCode || "",
    "commune de livraison*": location.communeAscii || "",
    "adresse de livraison*": order.address || "",
    "produit*": article,
    "poids (kg)": 1,
    "montant du colis*": order.total ?? 0,
    remarque: order.note || "",
    "FRAGILE ( si oui mettez OUI sinon laissez vide )": "",
    "ESSAYAGE PERMI ( si oui mettez OUI, sinon laissez vide )": "",
    "ECHANGE ( si oui mettez OUI sinon laissez vide )": "",
    "PICK UP ( si oui mettez OUI sinon laissez vide )": "",
    "RECOUVREMENT ( si oui mettez OUI sinon laissez vide )": "",
    "STOP DESK ( si oui mettez OUI sinon laissez vide )": order.stopdeskDelivery
      ? "OUI"
      : "",
    "Lien map": "",
  };

  // validation
  let isInvalid = false;

  // required columns (have *)
  const required = [
    "nom et prenom du destinataire*",
    "telephone*",
    "code wilaya*",
    "commune de livraison*",
    "adresse de livraison*",
    "produit*",
    "montant du colis*",
  ];

  for (const key of required) {
    const val = row[key];
    if (val === "" || val === null || val === undefined) {
      isInvalid = true;
      break;
    }
  }

  // phone format
  if (!isInvalid && row["telephone*"] && !isValidPhone(row["telephone*"])) {
    isInvalid = true;
  }

  // montant must be > 0
  if (
    !isInvalid &&
    (!row["montant du colis*"] || Number(row["montant du colis*"]) <= 0)
  ) {
    isInvalid = true;
  }

  // wilaya code not matched from ecotrack json
  if (!isInvalid && !location.matched) {
    isInvalid = true;
  }

  // stopdesk doesn't exist for this wilaya
  if (!isInvalid && order.stopdeskDelivery && !location.hasStopDesk) {
    isInvalid = true;
  }

  return { row, isInvalid };
}

function orderToZrExpressRow(order, location, hubName = "") {
  const { article, qty, ref } = getProductInfo(order);

  const phone = toInternationalPhone(order.phone);
  const note = (order.note && order.note.trim()) || "";
  const isStopdesk = !!order.stopdeskDelivery;
  const stopdeskName =
    hubName ||
    order.selectedDesk ||
    order.stopdeskName ||
    order.zrExpressSelectedHub ||
    "";

  const row = {
    "Nom Complet*": order.name || "",
    "Téléphone 1*": phone,
    "Téléphone 2": order.phone2 ? toInternationalPhone(order.phone2) : "",
    Produit: article,
    "Quantité*": qty,
    Sku: ref,
    "Type de stock": "",
    Adresse: order.address || "",
    Wilaya: location.wilayaAscii || "",
    Commune: location.communeAscii || "",
    "Prix total de la commande*": order.total ?? 0,
    Note: note || "vide",
    ID: order.orderNumber || "",
    "Stopdesk ( OUI )*": isStopdesk ? "OUI" : "NON",
    "Nom StopDesk": isStopdesk ? stopdeskName : "",
  };

  let isInvalid = false;

  const required = [
    "Nom Complet*",
    "Téléphone 1*",
    "Quantité*",
    "Prix total de la commande*",
    "Stopdesk ( OUI )*",
    "Note",
  ];

  for (const key of required) {
    const val = row[key];
    if (val === "" || val === null || val === undefined) {
      isInvalid = true;
      break;
    }
  }

  if (!isInvalid && !isValidInternationalPhone(row["Téléphone 1*"])) {
    isInvalid = true;
  }

  if (!isInvalid && Number(row["Prix total de la commande*"]) <= 0) {
    isInvalid = true;
  }

  if (!isInvalid && isStopdesk && !stopdeskName) {
    isInvalid = true;
  }

  return { row, isInvalid };
}

function orderToOsenRow(order, location) {
  const { article, qty } = getProductInfo(order);

  const row = {
    "Type De livraison": order.stopdeskDelivery ? "STOPDESK" : "DOORSTEP",
    "Nom De Client Destinataire": order.name || "",
    "Telephone De Client Destinataire": order.phone || "",
    "Wilaya De Client Destinataire": location.wilayaCode || "",
    "De Client Destinataire Commune francais": location.communeAscii || "",
    "Adress De Client Destinataire": order.address || "",
    "Description de colis": article,
    Reference: order.orderNumber || "",
    "Prix De Colis": order.total ?? 0,
    Fragile: "",
    ouverable: "",
    essayable: "",
    poids: 1,
    Volume: "",
  };

  // validation
  let isInvalid = false;

  const required = [
    "Nom De Client Destinataire",
    "Telephone De Client Destinataire",
    "Wilaya De Client Destinataire",
    "De Client Destinataire Commune francais",
    "Prix De Colis",
  ];

  for (const key of required) {
    const val = row[key];
    if (val === "" || val === null || val === undefined) {
      isInvalid = true;
      break;
    }
  }

  // name must contain only letters (no numbers)
  if (!isInvalid) {
    const name = String(row["Nom De Client Destinataire"] || "").trim();
    const nameOk = /^[\u0600-\u06FFa-zA-Z\s'.-]+$/.test(name);
    if (!nameOk) {
      isInvalid = true;
    }
  }

  if (!isInvalid && Number(row["Prix De Colis"]) <= 0) {
    isInvalid = true;
  }

  return { row, isInvalid };
}

function orderToGuepexRow(
  order,
  location,
  departureWilaya = "",
  isEconomy = false,
) {
  const { article } = getProductInfo(order);
  const { nom, prenom } = splitName(order.name);

  const phones = [order.phone, order.phone2].filter(Boolean).join(", ");

  const declaredValue = Array.isArray(order.products)
    ? order.products.reduce(
        (sum, p) => sum + (Number(p.price) || 0) * (Number(p.quantity) || 1),
        0,
      )
    : 0;

  const row = {
    "Wilaya de départ": departureWilaya,
    nom: nom,
    prénom: prenom,
    "téléphone (si plusieurs numéro, séparez les par une virgule)": phones,
    adresse: order.address || "",
    "commune (nom)": location.communeAscii || "",
    "wilaya (nom)": location.wilayaAscii || "",
    "STOP DESK (si oui mettez l'ID du stopdesk)": order.stopdeskDelivery
      ? order.selectedDesk || order.stopdeskCode || order.ecomStopdeskCode || ""
      : "",
    numero_commande: order.orderNumber || "",
    produit: article,
    prix: order.total ?? 0,
    "Assurer le colis ? (oui ou non) voir condition dans le site": "non",
    "valeur déclarée (la valeur du contenu du colis)": declaredValue,
    "longueur (en CM) facultatif sauf si surpoids": "",
    "largeur (en CM) facultatif sauf si surpoids": "",
    "hauteur (en CM) facultatif sauf si surpoids": "",
    "poids (en KG) facultatif sauf si surpoids": "1",
    "livraison gratuite (si oui mettez OUI sinon laissez vide)": "",
    "FAIRE UN ECHANGE? (si oui mettez OUI sinon laissez vide)": "",
    "OBJET A RECUPERER": "",
    "économique? (si oui mettez OUI sinon laissez vide)": isEconomy
      ? "OUI"
      : "",
  };

  // validation
  let isInvalid = false;

  const required = [
    "nom",
    "téléphone (si plusieurs numéro, séparez les par une virgule)",
    "commune (nom)",
    "wilaya (nom)",
    "prix",
  ];

  for (const key of required) {
    const val = row[key];
    if (val === "" || val === null || val === undefined) {
      isInvalid = true;
      break;
    }
  }

  if (!isInvalid && Number(row.prix) <= 0) {
    isInvalid = true;
  }

  if (!isInvalid && !location.matched) {
    isInvalid = true;
  }

  return { row, isInvalid };
}

function orderToMaystroSingleRow(order, location) {
  const { qty } = getProductInfo(order);

  const row = {
    Quantity: qty,
    Nom_du_client: order.name || "",
    Téléphone_du_client: order.phone || "",
    Adresse_de_livraison: order.address || "",
    wilaya: location.wilayaAscii || "",
    commune: location.communeAscii || "",
    Prix_total: order.total ?? 0,
    livraison_express: "no",
    Note_au_livreur: order.note || "",
    type_de_livraison: order.stopdeskDelivery ? "stop desk" : "domicile",
  };

  let isInvalid = false;
  const required = [
    "Nom_du_client",
    "Téléphone_du_client",
    "wilaya",
    "commune",
    "Prix_total",
  ];
  for (const key of required) {
    const val = row[key];
    if (val === "" || val === null || val === undefined) {
      isInvalid = true;
      break;
    }
  }
  if (!isInvalid && Number(row.Prix_total) <= 0) isInvalid = true;
  if (!isInvalid && !location.matched) isInvalid = true;

  return { row, isInvalid };
}

function orderToMaystroMultiRow(order, location, maxProducts) {
  const products = Array.isArray(order.products) ? order.products : [];

  const row = {
    id: order.orderNumber || "",
    Nom_du_client: order.name || "",
    Téléphone_du_client: order.phone || "",
    Adresse_de_livraison: order.address || "",
    wilaya: location.wilayaAscii || "",
    commune: location.communeAscii || "",
    Prix_total: order.total ?? 0,
    livraison_express: "no",
    Note_au_livreur: order.note || "",
    type_de_livraison: order.stopdeskDelivery ? "stop desk" : "domicile",
  };

  // Produit 1..n or Quantity 1..n
  for (let i = 1; i <= maxProducts; i++) {
    const p = products[i - 1];
    row[`Produit ${i}`] = p?.name || "";
    row[`Quantity ${i}`] = p?.quantity ?? "";
  }

  let isInvalid = false;
  const required = [
    "Nom_du_client",
    "Téléphone_du_client",
    "wilaya",
    "commune",
    "Prix_total",
  ];
  for (const key of required) {
    const val = row[key];
    if (val === "" || val === null || val === undefined) {
      isInvalid = true;
      break;
    }
  }
  if (!isInvalid && Number(row.Prix_total) <= 0) isInvalid = true;
  if (!isInvalid && !location.matched) isInvalid = true;
  if (!isInvalid && !row["Produit 1"]) isInvalid = true;

  return { row, isInvalid };
}

// main export
async function exportEcomDelivery(selectedOrders, deskCodesByOrderId = {}) {
  const response = await fetch(ECOM.templateUrl);
  if (!response.ok) {
    throw new Error(`Template not found: ${ECOM.templateUrl}`);
  }

  const buffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(ECOM.sheetName);
  if (!sheet) {
    throw new Error(`Sheet "${ECOM.sheetName}" missing in template`);
  }

  const { headerRowNumber, headerMap } = findHeaderMap(
    sheet,
    ECOM.anchorHeader,
  );

  if (!headerMap[ECOM.anchorHeader]) {
    throw new Error(`Header "${ECOM.anchorHeader}" not found`);
  }

  const startDataRow = headerRowNumber + 1;
  clearDataRows(sheet, startDataRow);

  selectedOrders.forEach((order, index) => {
    const location = resolveEcomLocation(order.state, order.commune);
    const stopdeskCode = resolveStopdeskCode(order, deskCodesByOrderId);
    const rowObj = orderToEcomRow(order, location, stopdeskCode);
    const isInvalid = !location.matched;

    writeRowToSheet(sheet, startDataRow + index, headerMap, rowObj, isInvalid);
  });

  const outBuffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const filename = `ecom_delivery_orders_${date}.xlsx`;
  downloadBuffer(outBuffer, filename);

  return { count: selectedOrders.length, filename };
}

async function exportEcotrack(selectedOrders, companyKey = "ecotrack") {
  const response = await fetch(ECOTRACK.templateUrl);
  if (!response.ok) {
    throw new Error(`Template not found: ${ECOTRACK.templateUrl}`);
  }

  const buffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(ECOTRACK.sheetName);
  if (!sheet) {
    throw new Error(`Sheet "${ECOTRACK.sheetName}" missing in template`);
  }

  const { headerRowNumber, headerMap } = findHeaderMap(
    sheet,
    ECOTRACK.anchorHeader,
  );

  if (!headerMap[ECOTRACK.anchorHeader]) {
    throw new Error(`Header "${ECOTRACK.anchorHeader}" not found`);
  }

  const startDataRow = headerRowNumber + 1;
  clearDataRows(sheet, startDataRow);

  selectedOrders.forEach((order, index) => {
    const location = resolveEcotrackWilaya(order.state, order.commune);
    const { row, isInvalid } = orderToEcotrackRow(order, location);

    if (isInvalid) {
      const problems = [];
      if (!order.name) problems.push("missing name");
      if (!order.phone) problems.push("missing phone");
      if (order.phone && !isValidPhone(order.phone))
        problems.push("invalid phone format");
      if (!location.wilayaCode) problems.push("missing wilaya code");
      if (!location.matched)
        problems.push("commune not matched in data_ecotrack.json");
      if (!location.communeAscii) problems.push("missing commune");
      if (!order.address) problems.push("missing address");
      if (!row["produit*"]) problems.push("missing product");
      if (!order.total || Number(order.total) <= 0)
        problems.push("invalid montant");
    }

    writeRowToSheet(sheet, startDataRow + index, headerMap, row, isInvalid);
  });

  const outBuffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const filename = `${companyKey}_orders_${date}.xlsx`;
  downloadBuffer(outBuffer, filename);

  return { count: selectedOrders.length, filename };
}

async function exportZrExpress(selectedOrders, hubNamesByOrderId = {}) {
  const response = await fetch(ZREXPRESS.templateUrl);
  if (!response.ok) {
    throw new Error(`Template not found: ${ZREXPRESS.templateUrl}`);
  }

  const buffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(ZREXPRESS.sheetName);
  if (!sheet) {
    throw new Error(`Sheet "${ZREXPRESS.sheetName}" missing in template`);
  }

  const { headerRowNumber, headerMap } = findHeaderMap(
    sheet,
    ZREXPRESS.anchorHeader,
  );

  if (!headerMap[ZREXPRESS.anchorHeader]) {
    throw new Error(`Header "${ZREXPRESS.anchorHeader}" not found`);
  }

  const startDataRow = headerRowNumber + 1;
  clearDataRows(sheet, startDataRow);

  selectedOrders.forEach((order, index) => {
    const location = resolveLocation(order.state, order.commune);
    const hubName = hubNamesByOrderId[order.id] || "";
    const { row, isInvalid } = orderToZrExpressRow(order, location, hubName);

    writeRowToSheet(sheet, startDataRow + index, headerMap, row, isInvalid);
  });

  const outBuffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const filename = `zrexpress_orders_${date}.xlsx`;
  downloadBuffer(outBuffer, filename);

  return { count: selectedOrders.length, filename };
}

async function exportOsen(selectedOrders) {
  const response = await fetch(OSEN.templateUrl);
  if (!response.ok) {
    throw new Error(`Template not found: ${OSEN.templateUrl}`);
  }

  const buffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(OSEN.sheetName);
  if (!sheet) {
    throw new Error(`Sheet "${OSEN.sheetName}" missing in template`);
  }

  const { headerRowNumber, headerMap } = findHeaderMap(
    sheet,
    OSEN.anchorHeader,
  );

  if (!headerMap[OSEN.anchorHeader]) {
    throw new Error(`Header "${OSEN.anchorHeader}" not found`);
  }

  const startDataRow = headerRowNumber + 1;
  clearDataRows(sheet, startDataRow);

  selectedOrders.forEach((order, index) => {
    const location = resolveLocation(order.state, order.commune);
    const { row, isInvalid } = orderToOsenRow(order, location);

    writeRowToSheet(sheet, startDataRow + index, headerMap, row, isInvalid);
  });

  const outBuffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const filename = `osen_orders_${date}.xlsx`;
  downloadBuffer(outBuffer, filename);

  return { count: selectedOrders.length, filename };
}

async function exportGuepex(selectedOrders, options = {}) {
  const departureWilaya = options.departureWilaya || "";
  const isEconomy = !!options.isEconomy;
  const response = await fetch(GUEPEX.templateUrl);
  if (!response.ok) {
    throw new Error(`Template not found: ${GUEPEX.templateUrl}`);
  }

  const buffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(GUEPEX.sheetName);
  if (!sheet) {
    throw new Error(`Sheet "${GUEPEX.sheetName}" missing in template`);
  }

  const { headerRowNumber, headerMap } = findHeaderMap(
    sheet,
    GUEPEX.anchorHeader,
  );

  if (!headerMap[GUEPEX.anchorHeader]) {
    throw new Error(`Header "${GUEPEX.anchorHeader}" not found`);
  }

  const startDataRow = headerRowNumber + 1;
  clearDataRows(sheet, startDataRow);

  selectedOrders.forEach((order, index) => {
    const location = resolveGuepexLocation(order.state, order.commune);
    const { row, isInvalid } = orderToGuepexRow(
      order,
      location,
      departureWilaya,
      isEconomy,
    );
    writeRowToSheet(
      sheet,
      startDataRow + index,
      headerMap,
      row,
      isInvalid || !location.matched,
    );
  });

  const outBuffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const filename = `guepex_orders_${date}.xlsx`;
  downloadBuffer(outBuffer, filename);

  return { count: selectedOrders.length, filename };
}

async function exportMaystro(selectedOrders) {
  const useSingle = shouldUseSingleProductTemplate(selectedOrders);
  const cfg = useSingle ? MAYSTRO : MAYSTRO_MULTI;

  const response = await fetch(cfg.templateUrl);
  if (!response.ok) {
    throw new Error(`Template not found: ${cfg.templateUrl}`);
  }

  const buffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const sheet = workbook.getWorksheet(cfg.sheetName);
  if (!sheet) {
    throw new Error(`Sheet "${cfg.sheetName}" missing in template`);
  }

  let { headerRowNumber, headerMap } = findHeaderMap(sheet, cfg.anchorHeader);
  if (!headerMap[cfg.anchorHeader]) {
    throw new Error(`Header "${cfg.anchorHeader}" not found`);
  }

  let maxProducts = 3;
  if (!useSingle) {
    maxProducts = Math.max(3, getMaxProductsCount(selectedOrders));
    headerMap = ensureMaystroMultiProductColumns(
      sheet,
      headerMap,
      headerRowNumber,
      maxProducts,
    );
  }

  const wilayaMap = buildMaystroWilayaMap(workbook, cfg.wilayasSheetName);

  const startDataRow = headerRowNumber + 1;
  clearDataRows(sheet, startDataRow);

  selectedOrders.forEach((order, index) => {
    const location = resolveMaystroLocation(
      order.state,
      order.commune,
      wilayaMap,
    );
    const { row, isInvalid } = useSingle
      ? orderToMaystroSingleRow(order, location)
      : orderToMaystroMultiRow(order, location, maxProducts);

    writeRowToSheet(
      sheet,
      startDataRow + index,
      headerMap,
      row,
      isInvalid || !location.matched,
    );
  });

  const outBuffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const filename = useSingle
    ? `maystro_orders_${date}.xlsx`
    : `maystro_multiple_products_orders_${date}.xlsx`;
  downloadBuffer(outBuffer, filename);

  return { count: selectedOrders.length, filename };
}

/**
 * @param {string} companyKey
 * @param {Array} selectedOrders
 * @param {{ deskCodesByOrderId?: Record<string, string> }} [options]
 */
export async function exportOrdersByCompany(
  companyKey,
  selectedOrders,
  options = {},
) {
  if (!companyKey) {
    return;
  }
  if (!selectedOrders?.length) {
    return;
  }

  switch (companyKey) {
    case "ecomdelivery":
      return exportEcomDelivery(
        selectedOrders,
        options.deskCodesByOrderId || {},
      );
    case "ecotrack":
      return exportEcotrack(selectedOrders);
    case "nordetouest":
      return exportEcotrack(selectedOrders, "nordetouest");
    case "zrexpress":
      return exportZrExpress(selectedOrders, options.hubNamesByOrderId || {});
    case "osen":
      return exportOsen(selectedOrders);
    case "guepex":
      return exportGuepex(selectedOrders, {
        departureWilaya: options.departureWilaya,
        isEconomy: options.isEconomy,
      });
    case "maystro":
      return exportMaystro(selectedOrders);

    default:
      console.warn(
        `[exportByCompany] Export not implemented yet for: ${companyKey}`,
      );
  }
}

export default exportOrdersByCompany;
